import { useState, useCallback, useMemo, type DragEvent } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge, type BadgeVariant } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { apiClient } from '@/lib/api-client';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { FolderTree } from './FolderTree';
import { DocumentRequestForm } from './DocumentRequestForm';
import { UploadDocumentsModal } from './UploadDocumentsModal';
import { ShareDocumentsModal } from './ShareDocumentsModal';
import { useContactsList } from '@/features/contacts/hooks/useContacts';
import { JobSearchModal, type JobSearchResult } from '@/components/JobSearchModal';
import {
  useDocumentsList,
  useDeleteDocument,
  useFolderTree,
  type Document,
  type DocumentsListParams,
  type ScanStatus,
  type Folder,
} from './hooks/useDocuments';
import type { FolderSelectContext } from './FolderTree';

/** Map scan status to badge variant */
const SCAN_STATUS_VARIANTS: Record<ScanStatus, BadgeVariant> = {
  pending: 'default',
  scanning: 'info',
  clean: 'success',
  infected: 'danger',
  error: 'warning',
};

const SCAN_STATUS_LABELS: Record<ScanStatus, string> = {
  pending: 'Pending',
  scanning: 'Scanning',
  clean: 'Clean',
  infected: 'Infected',
  error: 'Error',
};

/**
 * Format file size in human-readable format.
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

/**
 * Get file type icon based on mime type.
 */
function getFileTypeLabel(mimeType: string): string {
  if (mimeType.startsWith('image/')) return 'Image';
  if (mimeType === 'application/pdf') return 'PDF';
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'Spreadsheet';
  if (mimeType.includes('document') || mimeType.includes('word')) return 'Document';
  if (mimeType.includes('zip') || mimeType.includes('archive')) return 'Archive';
  return 'File';
}

/**
 * Documents page with split layout: folder tree on left, file list on right.
 * Supports drag-and-drop upload, filtering by client/job, and document requests.
 * Validates: Requirements 9.16, 9.3
 */
export function DocumentsPage() {
  const { can } = usePermissions();

  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  const [isClientFolder, setIsClientFolder] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const [params, setParams] = useState<DocumentsListParams>({
    page: 1,
    pageSize: 25,
    search: '',
    clientId: '',
    jobId: '',
  });

  const { data, isLoading } = useDocumentsList({
    ...params,
    folderId: isClientFolder ? undefined : activeFolderId,
  });

  const deleteDocument = useDeleteDocument();

  // Fetch the nested folder tree so we can auto-select the correct client
  // folder when the dropdown changes.
  const { data: foldersData } = useFolderTree();

  // Fetch clients for filter dropdown
  const { data: clientsData } = useContactsList({ contactTypeNature: 'client', pageSize: 200 });

  const [isJobSearchOpen, setIsJobSearchOpen] = useState(false);
  const [selectedJobFilter, setSelectedJobFilter] = useState<JobSearchResult | null>(null);
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [showShareModal, setShowShareModal] = useState(false);

  const clientFilterOptions = useMemo(() => {
    const options = (clientsData?.contacts ?? []).map((c) => ({
      value: c.id,
      label: `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || c.email || c.id,
    }));
    return [{ value: '', label: 'All Clients' }, ...options];
  }, [clientsData]);

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const handleFolderSelect = useCallback(
    (folderId: string | null, context?: FolderSelectContext) => {
      setActiveFolderId(folderId);

      if (folderId && context?.isClientRoot && context.clientId) {
        // Client root folder: show ALL of the client's documents (filter by
        // client, not by a single folder_id).
        setIsClientFolder(true);
        setParams((prev) => ({ ...prev, page: 1, clientId: context.clientId ?? '' }));
      } else if (folderId) {
        // A subfolder: filter to documents in exactly this folder, scoped to
        // its owning client so uploads land in the right place.
        setIsClientFolder(false);
        setParams((prev) => ({ ...prev, page: 1, clientId: context?.clientId ?? '' }));
      } else {
        // "All Documents" root.
        setIsClientFolder(false);
        setParams((prev) => ({ ...prev, page: 1, clientId: '' }));
      }
    },
    [],
  );

  /**
   * When the top client filter dropdown changes, navigate to that client's
   * folder in the tree (if one exists). If "All Clients" is selected or no
   * folder exists for the client, reset to "All Documents" view.
   */
  const handleClientFilterChange = useCallback(
    (clientId: string) => {
      if (clientId && foldersData) {
        // Find the root folder for this client in the folder tree
        const findClientFolder = (folders: Folder[]): Folder | undefined => {
          for (const folder of folders) {
            if (folder.clientId === clientId && !folder.parentId) {
              return folder;
            }
            if (folder.children?.length) {
              const found = findClientFolder(folder.children);
              if (found) return found;
            }
          }
          return undefined;
        };

        const clientFolder = findClientFolder(foldersData);
        if (clientFolder) {
          // Navigate to the client's folder
          setActiveFolderId(clientFolder.id);
          setIsClientFolder(true);
          setParams((prev) => ({ ...prev, clientId, page: 1 }));
          return;
        }
      }

      // No matching folder or "All Clients" selected — reset folder selection
      setActiveFolderId(null);
      setIsClientFolder(false);
      setParams((prev) => ({ ...prev, clientId, page: 1 }));
    },
    [foldersData],
  );

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    // Stage dropped files in the upload modal so the user can review before
    // uploading, rather than firing uploads immediately.
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setDroppedFiles(Array.from(e.dataTransfer.files));
      setShowUploadModal(true);
    }
  }, []);

  const handleDelete = useCallback(
    async (documentId: string) => {
      if (window.confirm('Are you sure you want to delete this document?')) {
        await deleteDocument.mutateAsync(documentId);
      }
    },
    [deleteDocument],
  );

  return (
    <div className="flex h-full flex-col">
      {/* Page header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Documents</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage client documents and file requests
          </p>
        </div>
        <div className="flex items-center gap-3">
          {selectedDocIds.size > 0 && can(PERMISSIONS.DOCUMENTS_READ) && (
            <Button variant="outline" onClick={() => setShowShareModal(true)}>
              Share ({selectedDocIds.size})
            </Button>
          )}
          {can(PERMISSIONS.DOCUMENTS_CREATE_REQUESTS) && (
            <Button variant="outline" onClick={() => setShowRequestModal(true)}>
              Request Documents
            </Button>
          )}
          {can(PERMISSIONS.DOCUMENTS_UPLOAD) && (
            <Button
              onClick={() => {
                setDroppedFiles([]);
                setShowUploadModal(true);
              }}
            >
              Upload Files
            </Button>
          )}
        </div>
      </div>

      {/* Split layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel: Folder tree */}
        <aside className="w-64 flex-shrink-0 border-r border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900">
          <FolderTree activeFolderId={activeFolderId} onFolderSelect={handleFolderSelect} />
        </aside>

        {/* Right panel: File list */}
        <main className="flex flex-1 flex-col overflow-hidden">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4 border-b border-gray-200 px-6 py-3 dark:border-gray-700">
            <div className="w-48">
              <Input
                placeholder="Search files..."
                value={params.search}
                onChange={(e) =>
                  setParams((prev) => ({ ...prev, search: e.target.value, page: 1 }))
                }
              />
            </div>
            <div className="w-40">
              <Select
                label=""
                options={clientFilterOptions}
                value={params.clientId ?? ''}
                onChange={(e) => handleClientFilterChange(e.target.value)}
              />
            </div>
            <div className="w-40">
              {selectedJobFilter ? (
                <div className="flex items-center gap-1 rounded-md border border-gray-300 bg-gray-50 px-2 py-1.5 text-xs dark:border-gray-600 dark:bg-gray-800">
                  <span className="flex-1 truncate text-gray-900 dark:text-gray-100">
                    {selectedJobFilter.jobCode || selectedJobFilter.title}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedJobFilter(null);
                      setParams((prev) => ({ ...prev, jobId: '', page: 1 }));
                    }}
                    className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    aria-label="Clear job filter"
                  >
                    <svg
                      className="h-3.5 w-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsJobSearchOpen(true)}
                  className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-left text-xs text-gray-500 hover:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400"
                >
                  All Jobs
                </button>
              )}
            </div>
          </div>

          {/* Drag-and-drop zone + file list */}
          <div
            className={`flex-1 overflow-y-auto p-6 transition-colors ${
              isDragOver
                ? 'bg-brand-50 ring-2 ring-inset ring-brand-300 dark:bg-brand-900/20 dark:ring-brand-700'
                : ''
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {/* Upload indicator */}
            {isDragOver && (
              <div className="pointer-events-none mb-4 flex items-center justify-center rounded-lg border-2 border-dashed border-brand-400 bg-brand-50 p-8 dark:border-brand-600 dark:bg-brand-900/30">
                <p className="text-sm font-medium text-brand-600 dark:text-brand-400">
                  Drop files here to upload
                </p>
              </div>
            )}

            {/* Loading state */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
              </div>
            )}

            {/* Empty state */}
            {!isLoading && (!data?.items || data.items.length === 0) && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <svg
                  className="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No documents in this folder
                </p>
                {can(PERMISSIONS.DOCUMENTS_UPLOAD) && (
                  <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                    Drag and drop files here or click Upload
                  </p>
                )}
              </div>
            )}

            {/* File list table */}
            {data?.items && data.items.length > 0 && (
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                    <tr>
                      <th className="w-10 px-4 py-3">
                        <input
                          type="checkbox"
                          checked={
                            data.items.length > 0 &&
                            data.items.every((doc: Document) => selectedDocIds.has(doc.id))
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedDocIds(new Set(data.items.map((doc: Document) => doc.id)));
                            } else {
                              setSelectedDocIds(new Set());
                            }
                          }}
                          className="h-4 w-4 rounded border-gray-300 text-brand-600"
                          aria-label="Select all documents"
                        />
                      </th>
                      <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">
                        Name
                      </th>
                      <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">
                        Type
                      </th>
                      <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">
                        Size
                      </th>
                      <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">
                        Uploaded By
                      </th>
                      <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">
                        Date
                      </th>
                      <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">
                        Scan Status
                      </th>
                      <th className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {data.items.map((doc: Document) => (
                      <tr
                        key={doc.id}
                        className="bg-white hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-800"
                      >
                        <td className="w-10 px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedDocIds.has(doc.id)}
                            onChange={(e) => {
                              setSelectedDocIds((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) {
                                  next.add(doc.id);
                                } else {
                                  next.delete(doc.id);
                                }
                                return next;
                              });
                            }}
                            className="h-4 w-4 rounded border-gray-300 text-brand-600"
                            aria-label={`Select ${doc.fileName}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {doc.fileName}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                          {getFileTypeLabel(doc.mimeType)}
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                          {formatFileSize(doc.fileSize)}
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                          {doc.uploadedByName}
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                          {new Date(doc.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={SCAN_STATUS_VARIANTS[doc.scanStatus]} size="sm">
                            {SCAN_STATUS_LABELS[doc.scanStatus]}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {can(PERMISSIONS.DOCUMENTS_READ) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={async () => {
                                  try {
                                    const response = await apiClient.get(
                                      `/documents/${doc.id}/preview`,
                                      { responseType: 'blob' },
                                    );
                                    const blob = new Blob([response.data as BlobPart], {
                                      type: doc.fileType || 'application/octet-stream',
                                    });
                                    const url = URL.createObjectURL(blob);
                                    window.open(url, '_blank');
                                  } catch {
                                    // Preview failed silently — apiClient handles 401 refresh
                                  }
                                }}
                                aria-label={`Preview ${doc.fileName}`}
                                title="Preview"
                              >
                                <svg
                                  className="h-4 w-4 text-gray-400 hover:text-brand-600"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                  />
                                </svg>
                              </Button>
                            )}
                            {can(PERMISSIONS.DOCUMENTS_DOWNLOAD) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={async () => {
                                  try {
                                    const response = await apiClient.get(
                                      `/documents/${doc.id}/download`,
                                      { responseType: 'blob' },
                                    );
                                    const blob = new Blob([response.data as BlobPart]);
                                    const url = URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.download = doc.fileName;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    URL.revokeObjectURL(url);
                                  } catch {
                                    // Silently fail
                                  }
                                }}
                                aria-label={`Download ${doc.fileName}`}
                                title="Download"
                              >
                                <svg
                                  className="h-4 w-4 text-gray-400 hover:text-green-600"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                  />
                                </svg>
                              </Button>
                            )}
                            {can(PERMISSIONS.DOCUMENTS_DELETE) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(doc.id)}
                                aria-label={`Delete ${doc.fileName}`}
                              >
                                <svg
                                  className="h-4 w-4 text-gray-400 hover:text-danger-500"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {data && data.total > data.pageSize && (
              <div className="mt-4 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {(data.page - 1) * data.pageSize + 1} to{' '}
                  {Math.min(data.page * data.pageSize, data.total)} of {data.total} documents
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={data.page <= 1}
                    onClick={() => setParams((prev) => ({ ...prev, page: (prev.page ?? 1) - 1 }))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={data.page * data.pageSize >= data.total}
                    onClick={() => setParams((prev) => ({ ...prev, page: (prev.page ?? 1) + 1 }))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Document Request Modal */}
      <Modal
        open={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        title="Request Documents from Client"
        size="3xl"
      >
        <DocumentRequestForm onSuccess={() => setShowRequestModal(false)} />
      </Modal>

      {/* Upload Documents Modal (staged multi-file upload) */}
      <UploadDocumentsModal
        key={showUploadModal ? `upload-${droppedFiles.length}-open` : 'upload-closed'}
        open={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setDroppedFiles([]);
        }}
        folderId={isClientFolder ? null : activeFolderId}
        clientId={params.clientId || undefined}
        jobId={params.jobId || undefined}
        targetLabel={
          clientFilterOptions.find((o) => o.value === params.clientId && o.value)?.label ??
          'All Documents'
        }
        initialFiles={droppedFiles}
      />

      {/* Job Search Filter Modal */}
      {isJobSearchOpen && (
        <JobSearchModal
          onSelect={(job) => {
            setSelectedJobFilter(job);
            setParams((prev) => ({ ...prev, jobId: job.id, page: 1 }));
            setIsJobSearchOpen(false);
          }}
          onClose={() => setIsJobSearchOpen(false)}
          title="Filter by Job"
          defaultStatus=""
        />
      )}

      {/* Share Documents Modal */}
      <ShareDocumentsModal
        open={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setSelectedDocIds(new Set());
        }}
        documentIds={Array.from(selectedDocIds)}
        documentNames={
          data?.items
            ?.filter((doc: Document) => selectedDocIds.has(doc.id))
            .map((doc: Document) => doc.fileName) ?? []
        }
      />
    </div>
  );
}
